import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet, FileUp, FileText, Database, Network } from "lucide-react";
import { ExcelExportLabel, excelExportButtonA11y } from "@/components/excelExportLabel";
import * as dataIo from "@/lib/dataIo";
import { RestoreBackupWizard } from "@/components/RestoreBackupWizard";
import { formatLastBackupWhen, getLastBackupMeta } from "@/lib/lastBackupMeta";

/**
 * Barra reutilizable: plantilla Excel, importar, exportar Excel/CSV/PDF, respaldo JSON.
 * @param {"cells"|"members"|"guests"|"backup"} resource
 * @param {boolean} canImport — si false, oculta importar y plantilla (solo export / respaldo)
 * @param {boolean} [canExport] — en recurso "cells": si false, oculta exportar Excel/CSV/PDF de células (predeterminado true)
 * @param {boolean} [disableExport] — si true, oculta exportar (p. ej. miembros exportados desde el panel con filtro)
 * @param {boolean} [canRestore] — solo resource "backup": permite elegir JSON y restaurar (pastor/supervisor)
 * @param {() => void} [onOpenOrganizationChart] — solo resource "cells": abre el organigrama
 */
export function DataIoToolbar({
  resource,
  canImport = true,
  canExport = true,
  disableExport = false,
  canRestore = false,
  onCompleted,
  onOpenOrganizationChart,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastBackup, setLastBackup] = useState(() => getLastBackupMeta());

  const refreshLastBackup = () => {
    setLastBackup(getLastBackupMeta());
  };

  const run = async (label, fn) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      if (label) setMessage({ type: "ok", text: label });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("DataIoToolbar:", e);
      let text = dataIo.errorToUserMessage(e);
      try {
        const fromApi = await dataIo.extractAxiosErrorMessage(e);
        if (fromApi && String(fromApi).trim()) text = String(fromApi).trim();
      } catch {
        /* mantener text */
      }
      setMessage({ type: "err", text });
    } finally {
      setBusy(false);
    }
  };

  if (resource === "backup") {
    const whenLabel = lastBackup ? formatLastBackupWhen(lastBackup.downloadedAt) : null;

    return (
      <div className="space-y-2 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              void run("Respaldo descargado", async () => {
                await dataIo.downloadBackupJson();
                refreshLastBackup();
              });
            }}
          >
            <Database className="w-4 h-4 mr-1" />
            Respaldo JSON
          </Button>
          {canRestore ? (
            <RestoreBackupWizard
              busy={busy}
              onBusyChange={setBusy}
              onMessage={setMessage}
              onCompleted={() => {
                refreshLastBackup();
                onCompleted?.();
              }}
              onPreventiveBackupDone={refreshLastBackup}
            />
          ) : null}
        </div>
        <div className="text-sm text-gray-700">
          {whenLabel ? (
            <>
              <p>
                <span className="font-medium text-gray-800">Último respaldo: </span>
                {whenLabel}
              </p>
              {lastBackup?.fileName ? (
                <p className="text-gray-600">
                  <span className="font-medium text-gray-800">Archivo: </span>
                  {lastBackup.fileName}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-gray-600">Aún no se ha realizado ningún respaldo.</p>
          )}
        </div>
        {message && (
          <p className={`text-sm ${message.type === "err" ? "text-red-600" : "text-green-700"}`}>{message.text}</p>
        )}
      </div>
    );
  }

  const label =
    resource === "cells" ? "Células" : resource === "members" ? "Miembros" : "Invitados";

  const onPickFile = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await dataIo.uploadImport(resource, file);
      const errs = res.errors?.length || 0;
      setMessage({
        type: errs ? "warn" : "ok",
        text: `Importación finalizada: ${res.inserted} filas creadas. ${errs} filas con error.`,
      });
      if (errs && res.errors?.length) {
        console.warn("Detalle importación", res.errors);
      }
      onCompleted?.();
    } catch (err) {
      let text = dataIo.errorToUserMessage(err);
      try {
        const fromApi = await dataIo.extractAxiosErrorMessage(err);
        if (fromApi && String(fromApi).trim()) text = String(fromApi).trim();
      } catch {
        /* mantener text */
      }
      setMessage({ type: "err", text });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 py-2 border-b border-gray-200 mb-4">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-gray-600 mr-2">{label}</span>
        {canImport && (
          <>
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => run(null, () => dataIo.downloadTemplate(resource))}>
              <Download className="w-4 h-4 mr-1" />
              Plantilla Excel
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onPickFile}>
              <FileUp className="w-4 h-4 mr-1" />
              Importar Excel/CSV
            </Button>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} />
          </>
        )}
        {!disableExport && ((resource === "cells" && canExport) || resource !== "cells") && (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => run(null, () => dataIo.downloadExport(resource, "xlsx"))}
              {...excelExportButtonA11y}
            >
              <FileSpreadsheet className="w-4 h-4 shrink-0" aria-hidden />
              <ExcelExportLabel />
            </Button>
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => run(null, () => dataIo.downloadExport(resource, "csv"))}>
              <FileText className="w-4 h-4 mr-1" />
              Exportar CSV
            </Button>
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => run(null, () => dataIo.downloadExport(resource, "pdf"))}>
              <FileText className="w-4 h-4 mr-1" />
              Exportar PDF
            </Button>
          </>
        )}
        {resource === "cells" && typeof onOpenOrganizationChart === "function" && (
          <Button type="button" variant="default" size="sm" disabled={busy} onClick={onOpenOrganizationChart}>
            <Network className="w-4 h-4 mr-1" />
            Organigrama
          </Button>
        )}
      </div>
      {message && (
        <p className={`text-sm ${message.type === "err" ? "text-red-600" : message.type === "warn" ? "text-amber-700" : "text-green-700"}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
